import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useSchool } from '../../contexts/SchoolContext';

/**
 * Single source of truth for the active school's white-label branding.
 *
 * Why this exists: SchoolContext loads the school once (getUserInitData) and
 * caches it, only refreshing when the *active school* changes — never when the
 * school's own fields change. So editing branding on the settings page writes
 * to Firestore but the cached `school` in context stays stale, and nothing
 * re-themes or re-displays.
 *
 * This provider keeps ONE live subscription to the active school doc and exposes
 * its branding fields. Every branding consumer (theme controller, navbar logo,
 * settings card, profile page) reads from here, so the whole app reflects the
 * true, current branding — live, and correctly after reload — from a single
 * listener rather than N scattered onSnapshot calls.
 *
 * Falls back to the cached context copy until the first snapshot arrives.
 */
const BrandingContext = createContext(null);

const EMPTY = {
  brandingEnabled: false,
  logoUrl: null,
  logoStoragePath: null,
  primaryColour: null,
  secondaryColour: null,
  tertiaryColour: null,
};

function pickBranding(source) {
  if (!source) return { ...EMPTY };
  return {
    brandingEnabled: !!source.brandingEnabled,
    logoUrl: source.logoUrl || null,
    logoStoragePath: source.logoStoragePath || null,
    primaryColour: source.primaryColour || null,
    secondaryColour: source.secondaryColour || null,
    tertiaryColour: source.tertiaryColour || null,
  };
}

export function BrandingProvider({ children }) {
  const { school, schoolId } = useSchool();
  const activeId = schoolId || school?.id || null;
  const [live, setLive] = useState(null);

  useEffect(() => {
    if (!activeId) {
      setLive(null);
      return;
    }
    // New school selected — drop the previous doc's branding until this one answers.
    setLive(null);
    const unsub = onSnapshot(
      doc(db, 'schools', activeId),
      (snap) => setLive(pickBranding(snap.data())),
      (err) => console.error('[BrandingProvider] snapshot error:', err),
    );
    return unsub;
  }, [activeId]);

  const value = useMemo(() => {
    // Live doc wins once it arrives; fall back to the cached context copy meanwhile.
    const branding = live || pickBranding(school);
    return { ...branding, ready: live !== null };
  }, [live, school]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

/**
 * Read the active school's live branding. Always returns a full branding object
 * (never null) so consumers don't have to guard.
 */
export function useBranding() {
  const ctx = useContext(BrandingContext);
  return ctx || { ...EMPTY, ready: false };
}
